import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationSectionSelect } from '@/pages/education/components/EducationLookupSelects';

type ApiError = { response?: { data?: { message?: string } } };

type EligibleSection = {
  id: string;
  name: string;
  capacity: number;
  enrolled_count: number;
  seats_available: number;
};

type EnrollmentDetail = {
  id: string;
  enrollment_no: string;
  status: string;
  student_id: string;
  session_id: string;
  grade_level_id: string;
  student?: { admission_no?: string; first_name?: string; last_name?: string | null };
  session?: { code?: string; name?: string };
  grade?: { code?: string; name?: string };
  current_section?: { section_id?: string; section_name?: string } | null;
  eligible_sections?: EligibleSection[];
  allocation_history?: Array<{
    id: string;
    section_name?: string;
    effective_on?: string;
    ended_on?: string | null;
    reason?: string | null;
    is_current?: number | boolean;
  }>;
  can_transfer?: boolean;
  transfer_blocked_reason?: string | null;
};

const TRANSFER_REASONS = [
  'Academic performance grouping',
  'Sibling alignment',
  'Teacher recommendation',
  'Parent request',
  'Capacity balancing',
  'Behaviour / pastoral',
  'Other',
];

export default function SectionTransferPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const back = `/education/enrollments/${id}?${params.toString()}`;

  const enrollment = useQuery({
    queryKey: ['education-enrollment', id],
    enabled: Boolean(id),
    queryFn: () => api.get<EnrollmentDetail>(`/education/enrollments/${id}`),
  });

  const detail = enrollment.data?.data;
  const studentName = [detail?.student?.first_name, detail?.student?.last_name].filter(Boolean).join(' ');
  const blocked = Boolean(detail && !detail.can_transfer);
  const sections = (detail?.eligible_sections ?? []).filter(
    (row) => row.id !== detail?.current_section?.section_id
  );

  const transfer = useMutation({
    mutationFn: (values: { section_id: string; reason: string; reason_detail?: string; effective_on: dayjs.Dayjs }) =>
      api.post(`/education/enrollments/${id}/transfer-section`, {
        section_id: values.section_id,
        reason: values.reason_detail?.trim()
          ? `${values.reason}: ${values.reason_detail.trim()}`
          : values.reason,
        effective_on: values.effective_on.format('YYYY-MM-DD'),
      }),
    onSuccess: () => {
      message.success(t('sectionTransferred'));
      queryClient.invalidateQueries({ queryKey: ['education-enrollment', id] });
      queryClient.invalidateQueries({ queryKey: ['education-lookup-sections'] });
      navigate(back);
    },
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={t('transferSection')}
      description={t('transferDetailIntro', {
        defaultValue:
          'Move an active student to another section of the same grade and session. Destination capacity is checked. History is retained.',
      })}
      tags={detail ? <Tag color={detail.can_transfer ? 'green' : 'orange'}>{detail.status}</Tag> : undefined}
      extra={<Button onClick={() => navigate(back)}>{t('backToEnrollments')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small" loading={enrollment.isLoading}>
          <Descriptions size="small" column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('enrollmentNo')}>{detail?.enrollment_no || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('status')}>
              {detail?.status ? <Tag>{detail.status}</Tag> : '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('student')}>
              {studentName || '—'}
              {detail?.student?.admission_no ? ` (${detail.student.admission_no})` : ''}
            </Descriptions.Item>
            <Descriptions.Item label={t('session')}>
              {detail?.session?.name || detail?.session?.code || '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('grade')}>
              {detail?.grade?.name || detail?.grade?.code || '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('currentSection', { defaultValue: 'Current section' })}>
              {detail?.current_section?.section_name || t('notAllocated', { defaultValue: 'Not allocated' })}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {blocked && detail?.transfer_blocked_reason ? (
          <Alert
            type="warning"
            showIcon
            message={t('cannotTransfer', { defaultValue: 'Cannot transfer yet' })}
            description={detail.transfer_blocked_reason}
            action={
              !detail.current_section ? (
                <Button size="small" type="primary" onClick={() => navigate(`/education/enrollments/${id}/allocate?${params.toString()}`)}>
                  {t('allocateSection')}
                </Button>
              ) : null
            }
          />
        ) : null}

        <Card size="small" title={t('destinationOptions', { defaultValue: 'Destination options' })}>
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={sections}
            locale={{
              emptyText: t('noEligibleSections', {
                defaultValue: 'No matching sections — create them under Academic Sessions for this grade',
              }),
            }}
            columns={[
              { title: t('section'), dataIndex: 'name' },
              { title: t('capacity'), dataIndex: 'capacity', width: 100 },
              { title: t('enrolled', { defaultValue: 'Enrolled' }), dataIndex: 'enrolled_count', width: 100 },
              {
                title: t('seatsLeft', { defaultValue: 'Seats left' }),
                dataIndex: 'seats_available',
                width: 110,
                render: (v: number) => <Tag color={v > 0 ? 'green' : 'red'}>{v}</Tag>,
              },
            ]}
          />
        </Card>

        <Card size="small" title={t('transferSection')}>
          <Form
            form={form}
            layout="vertical"
            initialValues={{ effective_on: dayjs(), reason: TRANSFER_REASONS[0] }}
            onFinish={(values) => transfer.mutate(values)}
            style={{ maxWidth: 520 }}
            disabled={blocked}
          >
            <Form.Item name="section_id" label={t('destinationSection')} rules={[{ required: true }]}>
              <EducationSectionSelect
                sessionId={detail?.session_id}
                gradeLevelId={detail?.grade_level_id}
                onlyWithSeats
                excludeSectionId={detail?.current_section?.section_id}
              />
            </Form.Item>
            <Form.Item name="effective_on" label={t('effectiveOn')} rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
            </Form.Item>
            <Form.Item name="reason" label={t('reason')} rules={[{ required: true }]}>
              <Select options={TRANSFER_REASONS.map((value) => ({ value, label: value }))} />
            </Form.Item>
            <Form.Item name="reason_detail" label={t('reasonDetail', { defaultValue: 'Reason detail (optional)' })}>
              <Input.TextArea rows={2} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={transfer.isPending} disabled={blocked}>
              {t('transferSection')}
            </Button>
          </Form>
        </Card>

        {(detail?.allocation_history?.length ?? 0) > 0 ? (
          <Card size="small" title={t('allocationHistory', { defaultValue: 'Allocation history' })}>
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={detail?.allocation_history}
              columns={[
                { title: t('section'), dataIndex: 'section_name' },
                { title: t('effectiveOn'), dataIndex: 'effective_on', width: 140 },
                { title: t('endedOn', { defaultValue: 'Ended on' }), dataIndex: 'ended_on', width: 140 },
                { title: t('reason'), dataIndex: 'reason', ellipsis: true },
                {
                  title: t('status'),
                  dataIndex: 'is_current',
                  width: 100,
                  render: (v: number | boolean) =>
                    v ? <Tag color="green">{t('current', { defaultValue: 'Current' })}</Tag> : <Tag>{t('past', { defaultValue: 'Past' })}</Tag>,
                },
              ]}
            />
          </Card>
        ) : null}
      </Space>
    </EducationPageChrome>
  );
}
